import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEntry, createEntry, updateEntry, generateId, uploadImage } from '../../utils/api';

const newEntry = () => ({
    id: generateId(),
    category: 'photo',
    year: '',
    title: '',
    blocks: [],
});

const newBlock = (type) => {
    const base = { id: generateId(), type };
    if (type === 'paragraph') return { ...base, text: '' };
    if (type === 'figure') return { ...base, src: '', caption: '' };
    if (type === 'imgBox') return { ...base, images: [] };
};

const newImage = () => ({ id: generateId(), src: '', className: '' });

function AdminEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [entry, setEntry] = useState(id ? null : newEntry());
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [uploadingIds, setUploadingIds] = useState({});

    const handleImageUpload = async (file, uploadId, onSuccess) => {
        if (!file) return;
        setUploadingIds(prev => ({ ...prev, [uploadId]: true }));
        try {
            const { url } = await uploadImage(file);
            onSuccess(url);
        } catch (e) {
            setErr('이미지 업로드 실패: ' + e.message);
        } finally {
            setUploadingIds(prev => { const n = { ...prev }; delete n[uploadId]; return n; });
        }
    };

    useEffect(() => {
        if (!id) { setEntry(newEntry()); return; }
        getEntry(id)
            .then(data => setEntry(data))
            .catch(e => setErr(e.message))
            .finally(() => setLoading(false));
    }, [id]);

    const set = (field, val) => setEntry(e => ({ ...e, [field]: val }));

    const updateBlock = (bid, changes) => setEntry(e => ({
        ...e,
        blocks: e.blocks.map(b => b.id === bid ? { ...b, ...changes } : b),
    }));

    const removeBlock = (bid) => setEntry(e => ({
        ...e,
        blocks: e.blocks.filter(b => b.id !== bid),
    }));

    const moveBlock = (idx, dir) => {
        const ni = idx + dir;
        if (ni < 0 || ni >= entry.blocks.length) return;
        const blocks = [...entry.blocks];
        [blocks[idx], blocks[ni]] = [blocks[ni], blocks[idx]];
        setEntry(e => ({ ...e, blocks }));
    };

    const addBlock = (type) => setEntry(e => ({ ...e, blocks: [...e.blocks, newBlock(type)] }));

    const addImage = (bid) => setEntry(e => ({
        ...e,
        blocks: e.blocks.map(b => b.id === bid
            ? { ...b, images: [...b.images, newImage()] }
            : b),
    }));

    const updateImage = (bid, iid, changes) => setEntry(e => ({
        ...e,
        blocks: e.blocks.map(b => b.id === bid
            ? { ...b, images: b.images.map(img => img.id === iid ? { ...img, ...changes } : img) }
            : b),
    }));

    const removeImage = (bid, iid) => setEntry(e => ({
        ...e,
        blocks: e.blocks.map(b => b.id === bid
            ? { ...b, images: b.images.filter(img => img.id !== iid) }
            : b),
    }));

    const handleSave = async () => {
        if (!entry.year.trim() || !entry.title.trim()) {
            setErr('년도와 제목을 입력해주세요.');
            return;
        }
        setSaving(true);
        setErr('');
        try {
            if (id) {
                await updateEntry(id, entry);
            } else {
                await createEntry(entry);
            }
            navigate('/admin');
        } catch (e) {
            setErr(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="admin-content" style={{ textAlign: 'center', padding: 60 }}>불러오는 중...</div>;

    return (
        <div className="admin-content">
            <div className="admin-edit-header">
                <h2>{id ? '항목 수정' : '새 항목 추가'}</h2>
                <div className="admin-edit-header-btns">
                    <button className="admin-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? '저장 중...' : '저장'}
                    </button>
                    <button className="admin-btn-cancel" onClick={() => navigate('/admin')}>취소</button>
                </div>
            </div>

            {err && <div className="admin-error">{err}</div>}

            {/* 기본 정보 */}
            <div className="admin-meta-group">
                <div className="admin-field">
                    <label>카테고리</label>
                    <select value={entry.category} onChange={e => set('category', e.target.value)}>
                        <option value="photo">Photo</option>
                        <option value="art">Art</option>
                    </select>
                </div>
                <div className="admin-field">
                    <label>년도</label>
                    <input
                        type="text"
                        value={entry.year}
                        onChange={e => set('year', e.target.value)}
                        placeholder="2024"
                        style={{ width: 90 }}
                    />
                </div>
                <div className="admin-field admin-field-grow">
                    <label>제목</label>
                    <input
                        type="text"
                        value={entry.title}
                        onChange={e => set('title', e.target.value)}
                        placeholder="챕터 제목"
                    />
                </div>
            </div>

            {/* 블록 에디터 */}
            <div>
                <p className="admin-section-title">콘텐츠 블록</p>

                {entry.blocks.map((block, idx) => (
                    <div key={block.id} className="admin-block">
                        <div className="admin-block-header">
                            <span className="admin-block-badge">
                                {block.type === 'paragraph' ? '문단' : block.type === 'figure' ? '이미지 + 캡션' : '이미지 갤러리'}
                            </span>
                            <div className="admin-block-controls">
                                <button onClick={() => moveBlock(idx, -1)} disabled={idx === 0}>↑</button>
                                <button onClick={() => moveBlock(idx, 1)} disabled={idx === entry.blocks.length - 1}>↓</button>
                                <button className="admin-btn-del-block" onClick={() => removeBlock(block.id)}>삭제</button>
                            </div>
                        </div>

                        <div className="admin-block-body">
                            {/* 문단 */}
                            {block.type === 'paragraph' && (
                                <>
                                    {block.text.length > 0 && (
                                        <p className="admin-dropcap-hint">첫 글자 자동 강조: <strong>"{block.text.charAt(0)}"</strong></p>
                                    )}
                                    <div className="admin-field">
                                        <label>본문</label>
                                        <textarea
                                            value={block.text}
                                            onChange={e => updateBlock(block.id, { text: e.target.value })}
                                            placeholder="본문을 입력하세요. 첫 번째 글자는 자동으로 크게 표시됩니다."
                                            rows={8}
                                        />
                                    </div>
                                </>
                            )}

                            {/* figure */}
                            {block.type === 'figure' && (
                                <>
                                    <div className="admin-field">
                                        <label>이미지</label>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                value={block.src}
                                                onChange={e => updateBlock(block.id, { src: e.target.value })}
                                                placeholder="/assets/img/photo/2024_01.png"
                                                style={{ flex: 1 }}
                                            />
                                            <label style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                <input type="file" accept="image/*" style={{ display: 'none' }}
                                                    onChange={e => { const t = e.target; handleImageUpload(t.files[0], block.id, url => { updateBlock(block.id, { src: url }); t.value = ''; }); }}
                                                />
                                                <span className="admin-btn-upload">{uploadingIds[block.id] ? '업로드 중...' : '파일 선택'}</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="admin-field">
                                        <label>캡션 <small style={{ fontWeight: 400 }}>(HTML 허용: &lt;strong&gt;, &lt;br&gt; 등)</small></label>
                                        <input
                                            type="text"
                                            value={block.caption}
                                            onChange={e => updateBlock(block.id, { caption: e.target.value })}
                                            placeholder="이미지 설명"
                                        />
                                    </div>
                                    {block.src && (
                                        <div className="admin-img-preview">
                                            <img src={process.env.PUBLIC_URL + block.src} alt="preview" />
                                        </div>
                                    )}
                                </>
                            )}

                            {/* imgBox (Swiper 갤러리) */}
                            {block.type === 'imgBox' && (
                                <>
                                    <div className="admin-imglist">
                                        {block.images.map((img, ii) => (
                                            <div key={img.id} className="admin-imgitem">
                                                <span className="admin-imgitem-num">{ii + 1}</span>
                                                <input
                                                    type="text"
                                                    value={img.src}
                                                    onChange={e => updateImage(block.id, img.id, { src: e.target.value })}
                                                    placeholder="/assets/img/photo/2024_list_01.png"
                                                />
                                                <label style={{ cursor: 'pointer' }}>
                                                    <input type="file" accept="image/*" style={{ display: 'none' }}
                                                        onChange={e => { const t = e.target; handleImageUpload(t.files[0], img.id, url => { updateImage(block.id, img.id, { src: url }); t.value = ''; }); }}
                                                    />
                                                    <span className="admin-btn-upload">{uploadingIds[img.id] ? '...' : '업로드'}</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={img.className === 'imgH'}
                                                        onChange={e => updateImage(block.id, img.id, { className: e.target.checked ? 'imgH' : '' })}
                                                    />
                                                    세로형
                                                </label>
                                                <button className="admin-btn-del-img" onClick={() => removeImage(block.id, img.id)}>✕</button>
                                            </div>
                                        ))}
                                    </div>
                                    <button className="admin-btn-addimg" onClick={() => addImage(block.id)}>
                                        + 이미지 추가
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}

                <div className="admin-add-block">
                    <span>블록 추가:</span>
                    <button onClick={() => addBlock('paragraph')}>문단</button>
                    <button onClick={() => addBlock('figure')}>이미지 + 캡션</button>
                    <button onClick={() => addBlock('imgBox')}>이미지 갤러리</button>
                </div>
            </div>
        </div>
    );
}

export default AdminEdit;
